// apps/merchant-v3/src/types/auth.ts
//
// Phase 6 Wave 1 Task 03 — TypeScript shapes for the auth endpoints
// consumed by AuthProvider + LoginPage. Mirrors the Zod contracts in
// `.vg/phases/06-merchant-portal/API-CONTRACTS/post-api-v1-auth-*.md`
// and `get-api-v1-auth-me.md`. All shapes follow INTERFACE-STANDARDS
// success/error envelope (already unwrapped by `lib/api/client.ts` so
// these types describe the inner `data` object only).
//
// Phase 6 Wave 12 Task 42 — `mapMeToUser` / `mapLoginUserToUser` extended
// to mirror `status === 'suspended'` into the new `merchantSuspended` /
// `merchantSuspendReason` fields on the portal `User` shape. Same truth
// value as the legacy `suspended` boolean — the duplication is a binding
// affordance so the new SuspendBanner + MutationGuard provider can read
// `useUser.merchantSuspended` directly.
//
// vg-binding: PLAN:task-03
// vg-binding: PLAN:task-42
// vg-binding: P6.D-02
// vg-binding: P6.D-114
// vg-binding: P6.D-172
// vg-binding: P6.D-205
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: INTERFACE-STANDARDS:response-envelope

import type { LoyaltyTier, BillingMode, User } from '@/types/user';
import type { BillingInfo } from '@/types/settings';

// --- /auth/login ----------------------------------------------------------

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
  twoFactorCode?: string;
}

export interface LoginUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  loyaltyTier: LoyaltyTier | null;
  displayCurrency: 'USD' | 'EUR' | 'AUD' | 'CAD' | 'GBP';
  timezone: string;
  language: 'en' | 'vi' | 'fr';
  role: 'merchant' | 'team_member';
  billingMode: BillingMode;
  status: 'active' | 'suspended' | 'deactivated';
  twoFactorEnabled: boolean;
}

export interface LoginResponse {
  ok: true;
  data: {
    user: LoginUser;
    /** When true server REQUIRES the client to re-call /auth/login with
     *  `twoFactorCode` populated. */
    requires2FA: boolean;
    /** Optional server-supplied next URL — overrides client-side returnUrl. */
    redirectTo?: string;
  };
  message?: string;
  request_id?: string;
}

// --- /auth/me -------------------------------------------------------------

export interface MeUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  phone: string | null;
  role: 'merchant' | 'team_member';
  parentMerchantId: string | null;

  // Phase 1 PATCH
  // dbg-20260929-loyaltytier — nullable: GET /auth/me returns null for a
  // merchant with no tier row, and the badge is omitted rather than guessed.
  loyaltyTier: LoyaltyTier | null;

  // Phase 6 Amendment #1
  billingMode: BillingMode;
  status: 'active' | 'suspended' | 'deactivated';
  suspendReason: string | null;
  suspendContact: string | null;

  // R8-1 (2026-05-17, BUG-P6-DEBUG-B3): expose persisted billing info so
  // /settings/general can hydrate the BillingInfoForm on mount. BE GET
  // /auth/me returns this as `billingInfo` (apps/api/src/modules/auth/routes/me.routes.ts).
  // Was missing from MeUser → form always rendered empty on reload even
  // though the BE had the data.
  billingInfo: BillingInfo | null;

  // Display preferences
  language: 'en' | 'vi' | 'fr';
  timezone: string;
  displayCurrency: 'USD' | 'EUR' | 'AUD' | 'CAD' | 'GBP';
  theme: 'light' | 'dark' | 'system';
  // R9-4 (2026-05-17, BUG-P6-DEBUG-R2-B7) — row-density persisted via
  // PATCH /auth/me/preferences. Was missing from MeUser → reader always
  // defaulted to 'normal' on reload.
  density: 'compact' | 'normal';

  // Security
  twoFactorEnabled: boolean;
  emailVerified: boolean;

  // RBAC scopes
  permissions: string[];

  // Optional impersonation marker
  impersonatedBy: {
    adminId: string;
    adminName: string;
    startedAt: string;
  } | null;
}

export interface MeResponse {
  ok: true;
  data: MeUser;
}

// --- /auth/refresh --------------------------------------------------------

export interface RefreshResponse {
  ok: true;
  data: {
    expiresAt: string;
    user: { id: string; email: string };
  };
}

// --- /auth/logout ---------------------------------------------------------

export interface LogoutResponse {
  ok: true;
  data: null;
  message?: string;
}

// --- /auth/ws-ticket -------------------------------------------

export interface WsTicketResponse {
  ok: true;
  data: {
    ticket: string;
    wsUrl: string;
    expiresIn: number;
  };
}

// --- envelope mappers -----------------------------------------------------

/** Map a wire-shape MeUser → portal-internal `User` (Zustand store).
 *  Server emits lowercase language codes (`'vi'|'en'|'fr'`) per the Zod
 *  schema; the FE's `User.language` enum is uppercase (`'VI'|'EN'|'FR'`)
 *  to match the existing TopBar selector (Task 02). Wave 12 Task 42 also
 *  mirrors `status === 'suspended'` into the new `merchantSuspended` /
 *  `merchantSuspendReason` fields consumed by the persistent SuspendBanner
 *  + MutationGuard provider. */
export function mapMeToUser(me: MeUser): User {
  const lang = (me.language ?? 'en').toUpperCase() as User['language'];
  const isSuspended = me.status === 'suspended';
  return {
    id: me.id,
    name: me.name,
    email: me.email,
    avatarUrl: me.avatarUrl ?? undefined,
    loyaltyTier: me.loyaltyTier ?? null,
    billingMode: me.billingMode,
    suspended: isSuspended,
    merchantSuspended: isSuspended,
    merchantSuspendReason: me.suspendReason ?? undefined,
    displayCurrency: me.displayCurrency,
    language: lang,
    timezone: me.timezone,
  };
}

/** Map a /auth/login `data.user` payload → portal-internal `User`. */
export function mapLoginUserToUser(u: LoginUser): User {
  const lang = (u.language ?? 'en').toUpperCase() as User['language'];
  const isSuspended = u.status === 'suspended';
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    avatarUrl: u.avatarUrl ?? undefined,
    // The login payload does not carry a tier; /auth/me hydrates it a moment
    // later. `null` until then means "no badge yet", not "Titanium".
    loyaltyTier: u.loyaltyTier ?? null,
    billingMode: u.billingMode,
    suspended: isSuspended,
    merchantSuspended: isSuspended,
    // LoginUser does not carry the suspend reason today — banner falls back
    // to the generic copy until /auth/me hydrates the richer payload.
    merchantSuspendReason: undefined,
    displayCurrency: u.displayCurrency,
    language: lang,
    timezone: u.timezone,
  };
}
